<template>
    <div>
        <RedesignDataTableComponent
            :widget-data="data"
            :table-data="tableData"
            :columns="columnsMapped"
            :columns-config="columnsConfig"
            :stripe="stripe"
            :border="border"
            @update-widget="updateWidget"
            @update-columns-config="updateColumnsConfig"
            @update-headers-and-columns-config="updateColumnsAndHeadersConfig"
        >
            <template #cell="{ row, column }">
                <template v-if="column.prop === 'WaitingTime'">
                    <waiting-time
                        v-if="row.Call"
                        :key="row.Call.ivrid"
                        :call="row.Call"
                        :text-color="'text-white'"
                    />
                </template>
                <span
                    v-else
                    v-html="row[column.prop] || '--'"
                />
            </template>
        </RedesignDataTableComponent>
    </div>
</template>

<script lang="ts" setup>
import { ref, computed, defineEmits, toRef } from 'vue'
import { ColumnConfig, ColumnsObjectConfig, TableHeadersConfig, WidgetExtended, WidgetList } from '@/types/dashboard'
import { hasColumnSpecificAlignment } from '@/helpers/alignmentUtils'
import { QueueCallSDK } from '@voicenter-team/events-sdk'

import orderBy from 'lodash/orderBy'
import WaitingTime from './WaitingTime.vue'
import { activeCallColumns } from '@/enum/queueConfigs'
import { QUEUE_LIST_PARAMETER_NAME } from '@/enum/widgetTemplateConfigs'
import useQueues from '@/composables/useQueues'
import useEntityPositiveNegativeFilters from '@/composables/useEntityPositiveNegativeFilters'
import RedesignDataTableComponent from '@/components/RedesignBaseComponents/RedesignDataTableComponent.vue'
import useTableHeaderTranslations from '@/composables/useTableHeaderTranslations'
import { defaultColumnConfig } from '@/enum/redesignTableConfig'
/* Types */
interface Row {
    QueueName: string
    CallerNumber: string
    CallerName: string
    CallerID: string
    Call: QueueCallSDK
    QueueID: number
}

/* Props */
const props = withDefaults(
    defineProps<{
        data: WidgetList,
        editable?: boolean
    }>(),
    {
        editable: false
    }
)

const emit = defineEmits<{
    (event: 'on-update', payload: WidgetExtended): void
}>()

/* Data */
const border = ref(true)
const stripe = ref(true)
const widget = toRef(props, 'data')

const { makeTableHeaderTooltipTranslations, makeTableHeaderTranslations } = useTableHeaderTranslations()

/* Composable */
const { queueWithActiveCalls } = useQueues()
const { filteredItems: filteredQueuesWithActiveCalls } = useEntityPositiveNegativeFilters(widget, QUEUE_LIST_PARAMETER_NAME, queueWithActiveCalls, 'QueueID')
/* Computed */
const hideVirtualHoldCalls = computed(() => {
  return props.data.WidgetLayout?.hideVirtualHoldCalls ?? false
})
const tableData = computed(() => {
    const data: Array<Row> = []

    filteredQueuesWithActiveCalls.value.forEach((queue) => {
        queue.Calls?.forEach((call) => {
          if('VHJoinTimeStamp_UTC_CLIENT' in call && call.VHJoinTimeStamp_UTC_CLIENT && hideVirtualHoldCalls.value) {
            return
          }
            data.push({
                QueueName: queue.QueueName,
                CallerNumber: queue.CallerPhone || ' - ',
                CallerName: call.CallerName,
                CallerID: call.CallerID,
                QueueID: queue.QueueID,
                Call: call
            })
        })
    })

    return orderBy(
        data,
        function (q) {
            return q.Call.JoinTimeStamp_UTC_CLIENT
        },
        [ 'asc' ]
    )
})

const availableColumns = computed(() => {
    return activeCallColumns
})

const columnsConfig = computed(() => {
    const savedConfig = props.data.WidgetLayout?.columnsObjectConfig || {}
    const config: Record<string, ColumnConfig> = {}

    availableColumns.value.forEach((column, index) => {
        const defaultConfig: ColumnConfig = {
            ...defaultColumnConfig,
            showFullTextLabel: true,
            width: 'auto',
            order: index,
        }

        config[column.prop] = {
            ...defaultConfig,
            ...savedConfig[column.prop],
        }
    })
    return config
})

const columnsMapped = computed(() => {
    return availableColumns.value.map(column => {
        return {
            ...column,
            label: makeTableHeaderTranslations(column, column.label as string),
            tooltipLabel: makeTableHeaderTooltipTranslations(column, column.label as string)
        }
    })
})

/*Methods */
const updateColumnsConfig = (config: ColumnsObjectConfig) => {
    if (!config || !Object.values(config).length) {
        return
    }
    const hasColumnSpecificAlignmentValue = hasColumnSpecificAlignment(config, props.data.WidgetLayout?.tableColumnsAlignment)
    const updatedWidget = {
        ...props.data,
        WidgetLayout: {
            ...(props.data.WidgetLayout || {}),
            hasColumnSpecificAlignment: hasColumnSpecificAlignmentValue,
            columnsObjectConfig: config
        }
    }
    updateWidget(updatedWidget)
}
const updateColumnsAndHeadersConfig = (config: ColumnsObjectConfig, tableHeadersConfig: TableHeadersConfig) => {
    const hasColumnSpecificAlignmentValue = hasColumnSpecificAlignment(config, props.data.WidgetLayout?.tableColumnsAlignment)
    const updatedWidget = {
        ...props.data,
        WidgetLayout: {
            ...(props.data.WidgetLayout || {}),
            hasColumnSpecificAlignment: hasColumnSpecificAlignmentValue,
            ...(config && Object.values(config) ? { columnsObjectConfig: config } : {}),
            ...(tableHeadersConfig ? { tableHeadersConfig } : {})
        }
    }
    updateWidget(updatedWidget)
}

const updateWidget = (widgetData: WidgetExtended) => {
    emit('on-update', widgetData)
}
</script>
